import { z } from 'zod';
import { CITY_BY_CODE } from './cities';
import { MENU_MILESTONES } from './constants';

const DISPOSABLE_DOMAINS = [
  'yopmail.com', 'guerrillamail.com', 'mailinator.com', 'tempmail.com',
  'test.com', 'example.com', 'sample.com', 'throwaway.email', 'dispostable.com',
  'trashmail.com', 'sharklasers.com', 'maildrop.cc', '10minutemail.com',
  'burnermail.io', 'inboxkitten.com', 'temp-mail.org', 'mailsac.com',
  'nada.email', 'spam4.me', 'tempalias.com', 'tempemail.com',
  'temporaryinbox.com', 'vmail.me', 'zomg.info'
];

const SPAM_KEYWORDS = ['test', 'attack', 'hack', 'spam', 'flood', 'admin', 'pentest', 'bot'];

// ─── Auth Schemas ────────────────────────────────
const attributionTouchSchema = z.object({
  source: z.string().max(500).optional(),
  medium: z.string().max(500).optional(),
  campaign: z.string().max(500).optional(),
  landing_page: z.string().max(500).optional(),
  referrer: z.string().max(500).optional(),
  affiliate_code: z.string().max(500).optional(),
  referral_code: z.string().max(500).optional(),
  webinar_id: z.string().max(500).optional(),
  ts: z.string().max(500).optional(),
});

export const registerSchema = z.object({
  email: z.string()
    .email('Email tidak valid')
    .max(100, 'Email terlalu panjang')
    .refine((val) => {
      const domain = val.split('@')[1]?.toLowerCase();
      if (domain && DISPOSABLE_DOMAINS.includes(domain)) return false;
      if (domain === 'growwithai.id') return false;
      return true;
    }, 'Domain email tidak diizinkan untuk pendaftaran')
    .refine((val) => {
      const local = val.split('@')[0]?.toLowerCase();
      if (local && SPAM_KEYWORDS.some(kw => local.includes(kw))) return false;
      return true;
    }, 'Alamat email mengandung kata yang dilarang')
    .refine((val) => {
      if (/(.)\1{5,}/.test(val)) return false;
      return true;
    }, 'Format email terindikasi spam')
    .transform((val) => val.trim().toLowerCase()),
  // Opsional untuk guest checkout tanpa password: bila kosong, server membuat
  // password acak & user masuk lewat magic link. Bila diisi wajib ≥ 8 karakter.
  password: z.string().min(8, 'Password minimal 8 karakter').optional(),
  name: z.string()
    .min(2, 'Nama minimal 2 karakter')
    .max(100)
    .regex(/^[^<>]*$/, 'Nama tidak boleh mengandung simbol < atau > untuk keamanan'),
  phone: z.string().min(8, 'Nomor WhatsApp minimal 8 karakter')
    .refine((v) => !v.includes('@'), 'Nomor WhatsApp tidak boleh berupa alamat email'),
  referral_source: z.string().max(100).optional(),
  affiliate_code: z.string().max(60).optional(),
  /** Kode undangan (dipakai bila tenant registration_mode = 'invite_only'). */
  invite_code: z.string().max(120).optional(),
  attribution: z.object({
    // nullish (not optional): getAttribution() sends null — not undefined — for
    // first/last when no attribution is stored (direct/organic visits, or when
    // localStorage is blocked e.g. Safari private/ITP). .optional() rejects null
    // and broke registration for those users with a generic "Validation error".
    first: attributionTouchSchema.nullish(),
    last: attributionTouchSchema.nullish(),
  }).nullish(),
  cfTurnstileResponse: z.string().optional(),
  _hp_website: z.string().optional(),
});

export const loginSchema = z.object({
  email: z.string().email('Email tidak valid').transform((val) => val.trim().toLowerCase()),
  password: z.string().min(1, 'Password harus diisi'),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email('Email tidak valid').transform((val) => val.trim().toLowerCase()),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8, 'Password minimal 8 karakter'),
});

export const verifyEmailSchema = z.object({
  email: z.string().email('Email tidak valid').transform((val) => val.trim().toLowerCase()),
  otp: z.string().length(6, 'Kode OTP harus 6 digit'),
});

export const resendVerificationSchema = z.object({
  email: z.string().email('Email tidak valid').transform((val) => val.trim().toLowerCase()),
});

export const updateProfileSchema = z.object({
  name: z.string()
    .min(2, 'Nama minimal 2 karakter')
    .max(100, 'Nama maksimal 100 karakter')
    .regex(/^[^<>]*$/, 'Nama tidak boleh mengandung simbol < atau >'),
  phone: z.string().max(30, 'Nomor terlalu panjang')
    .refine((v) => !v.includes('@'), 'Nomor WhatsApp tidak boleh berupa alamat email')
    .optional().nullable(),
  avatar_url: z.string().url('URL avatar tidak valid').max(500).optional().nullable(),
  bio: z.string()
    .max(500, 'Deskripsi maksimal 500 karakter')
    .regex(/^[^<>]*$/, 'Deskripsi tidak boleh mengandung simbol < atau >')
    .optional().nullable(),
  city_code: z.string().max(60)
    .refine((v) => v in CITY_BY_CODE, 'Kota tidak valid')
    .optional().nullable(),
  map_visibility: z.enum(['hidden', 'anonymous', 'name', 'full']).optional(),
});

export const recordMilestoneSchema = z.object({
  milestone: z.enum(MENU_MILESTONES),
});

export const changePasswordSchema = z.object({
  current_password: z.string().min(1, 'Password saat ini harus diisi'),
  new_password: z.string().min(8, 'Password baru minimal 8 karakter'),
});

// ─── Course Schemas ──────────────────────────────
export const createCourseSchema = z.object({
  title: z.string().min(3, 'Judul minimal 3 karakter').max(200),
  slug: z.string().min(3).max(200).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash'),
  subtitle: z.string().max(300).optional(),
  description: z.string().optional(),
  short_description: z.string().max(500).optional(),
  thumbnail_url: z.string().url().optional(),
  level: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
  category: z.string().max(100).optional(),
  primary_category_id: z.string().max(50).nullable().optional(),
  secondary_category_ids: z.array(z.string().max(50)).max(2, 'Maksimal 2 secondary category').optional(),
  learning_outcome: z.string().max(1000).nullable().optional(),
  recommended_for: z.string().max(500).nullable().optional(),
  duration_minutes: z.number().int().min(0).nullable().optional(),
  instructor_name: z.string().max(100).optional(),
  // free = semua user; membership_only = member aktif ATAU pembeli; individual =
  // HANYA pembeli kelas ini (membership TIDAK membuka). 'paid' legacy = membership_only.
  access_type: z.enum(['free', 'paid', 'membership_only', 'individual']).default('paid'),
  price: z.number().int().min(0).default(0),
  compare_at_price: z.number().int().min(0).optional(),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
  sort_order: z.number().int().default(0),
});

export const updateCourseSchema = createCourseSchema.partial();

// ─── Module Schemas ──────────────────────────────
export const createModuleSchema = z.object({
  course_id: z.string().min(1),
  title: z.string().min(1, 'Judul modul harus diisi').max(200),
  description: z.string().optional(),
  sort_order: z.number().int().default(0),
});

export const updateModuleSchema = createModuleSchema.partial().omit({ course_id: true });

// ─── Lesson Schemas ──────────────────────────────
export const createLessonSchema = z.object({
  course_id: z.string().min(1),
  module_id: z.string().min(1),
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/),
  lesson_type: z.enum(['video', 'text', 'quiz', 'resource']).default('video'),
  video_provider: z.string().optional(),
  video_url: z.string().optional(),
  content_markdown: z.string().optional(),
  content_html: z.string().optional(),
  // admin/lessons.routes.ts (endpoint terpisah dari admin/courses.routes.ts)
  // membaca & menulis kolom ini juga — dulu absen di sini sehingga zod diam-diam
  // membuang content_embed sebelum sampai ke handler saat dipakai di sana.
  content_embed: z.string().optional(),
  content_format: z.enum(['html']).default('html'),
  description: z.string().optional(),
  resources_json: z.string().optional(),
  is_preview: z.number().int().min(0).max(1).default(0),
  sort_order: z.number().int().default(0),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
});

export const updateLessonSchema = createLessonSchema.partial().omit({ course_id: true, module_id: true });

export const reorderLessonsSchema = z.object({
  lessons: z.array(z.object({
    id: z.string().min(1),
    module_id: z.string().min(1),
    sort_order: z.number().int().min(0),
  })).min(1, 'Minimal 1 lesson untuk reorder').max(500),
});

// ─── Checkout Schemas ────────────────────────────
// order_type & addons permisif (bukan enum ketat): jenis order baru (mis.
// digital_product, tip) dan tipe addon ditambahkan di sisi service tanpa
// perlu menyentuh skema ini — validasi nilai sesungguhnya ada di prepareOrder.
export const createOrderSchema = z.object({
  order_type: z.string().min(1),
  product_id: z.string().min(1),
  variant_id: z.string().optional(),
  affiliate_code: z.string().optional(),
  mobile: z.string().optional(),
  coupon_code: z.string().optional(),
  addons: z.array(z.object({
    type: z.string().optional(),
    step_id: z.string().optional(),
  })).optional(),
  // PWYW/tip: nominal dari pembeli — clamp final tetap di prepareOrder.
  custom_amount: z.number().int().positive().optional(),
}).passthrough();

// ─── Membership Schemas ──────────────────────────
// benefits disimpan terpisah di app_settings (bukan kolom membership_plans),
// tapi tetap diterima di sini agar tak diam-diam dibuang zod (lihat
// adminMemberships di apps/api — sanitizeBenefits() yang membersihkannya).
export const createMembershipPlanSchema = z.object({
  slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  duration_type: z.enum(['monthly', 'yearly', 'lifetime', 'custom']),
  duration_days: z.number().int().min(1).optional(),
  price: z.number().int().min(0),
  compare_at_price: z.number().int().min(0).optional(),
  status: z.enum(['active', 'inactive']).default('active'),
  sort_order: z.number().int().default(0),
  benefits: z.array(z.string()).optional(),
});

export const updateMembershipPlanSchema = createMembershipPlanSchema.partial();

export const grantMembershipSchema = z.object({
  user_id: z.string().min(1),
  plan_id: z.string().min(1),
  notes: z.string().optional(),
});

export const membershipDaysSchema = z.object({
  days: z.number().int().positive('days harus angka positif'),
});

// ─── Bonus Schemas ───────────────────────────────
// Longgar dengan sengaja (bukan enum ketat untuk bonus_type/asset_type/status —
// nilai sesungguhnya di kode: status 'draft'|'active', bukan 'published'|
// 'archived' seperti skema lama yang tak pernah dipakai). Guard bisnis (mis.
// status 'active' wajib primary_category_id+asset_type) tetap di route,
// bukan di skema — itu aturan lintas-field, bukan bentuk input.
export const createBonusSchema = z.object({
  slug: z.string().max(200).optional(),
  title: z.string().min(1, 'Judul bonus wajib diisi').max(200),
  description: z.string().optional().nullable(),
  bonus_type: z.string().optional(),
  asset_type: z.string().optional().nullable(),
  primary_category_id: z.string().optional().nullable(),
  secondary_category_ids: z.array(z.string()).optional(),
  recommended_for: z.string().optional().nullable(),
  related_course_id: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  r2_key: z.string().optional().nullable(),
  external_url: z.string().optional().nullable(),
  required_plan_ids: z.array(z.string()).optional(),
  access_type: z.string().optional(),
  status: z.string().optional(),
  sort_order: z.number().int().optional(),
}).passthrough();

export const updateBonusSchema = createBonusSchema.partial();

export const createBonusFileSchema = z.object({
  title: z.string().min(1, 'Judul file wajib diisi').max(200),
  file_type: z.string().optional(),
  file_url: z.string().optional().nullable(),
  r2_key: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  r2_thumbnail_key: z.string().optional().nullable(),
  sort_order: z.number().int().optional(),
});

// ─── Webinar Schemas ─────────────────────────────
// Sebelumnya field ini duplikat + lebih lengkap secara ad-hoc di
// admin/webinars.routes.ts (bukan diimpor dari sini). Skema versi lama di sini
// TIDAK mendeklarasikan free_registration_enabled/paid_registration_enabled/
// paid_registration_price/share_required_count/share_message_template/
// short_link_enabled — karena z.object() default membuang key tak dikenal,
// field itu DIAM-DIAM ter-strip dari body sebelum sampai ke handler, jadi
// admin tak pernah bisa mengubahnya lewat form (selalu jatuh ke default
// ??). Digabung di sini jadi satu skema lengkap + preprocessing
// trim/normalisasi URL yang sudah ada di versi route-nya.
function trimInput(value: unknown): unknown {
  return typeof value === 'string' ? value.trim() : value;
}

function normalizeUrlInput(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const webinarOptionalText = (max: number) => z.preprocess(
  trimInput,
  z.string().max(max).optional().or(z.literal(''))
);

const webinarOptionalUrl = z.preprocess(
  normalizeUrlInput,
  z.string().url().optional().or(z.literal(''))
);

// Beda dari webinarOptionalUrl: redirect pasca-pendaftaran boleh path internal
// (diawali /) SELAIN URL absolut — beda kebutuhan dari join_url/calendar_url
// dkk yang selalu link eksternal penuh.
const webinarOptionalRedirect = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() : value),
  z.string().max(500).optional().or(z.literal(''))
    .refine((v) => !v || v.startsWith('/') || /^https?:\/\//i.test(v), {
      message: 'Redirect harus path internal (diawali /) atau URL lengkap (https://...)',
    })
);

export const createWebinarSchema = z.object({
  title: z.preprocess(trimInput, z.string().min(3, 'Judul minimal 3 karakter').max(200)),
  slug: z.preprocess(trimInput, z.string().min(3).max(200).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash')),
  subtitle: webinarOptionalText(300),
  body_html: z.preprocess(trimInput, z.string().optional()),
  thumbnail_url: webinarOptionalUrl,
  host_name: webinarOptionalText(120),
  starts_at: z.preprocess(trimInput, z.string().min(1, 'Tanggal mulai wajib diisi')),
  ends_at: z.preprocess(trimInput, z.string().optional().or(z.literal(''))),
  timezone: z.preprocess(trimInput, z.string().max(80).default('Asia/Jakarta')),
  capacity: z.number().int().min(1).optional().nullable(),
  join_url: webinarOptionalUrl,
  calendar_url: webinarOptionalUrl,
  recording_url: webinarOptionalUrl,
  recording_embed_url: webinarOptionalUrl,
  replay_body_html: z.preprocess(trimInput, z.string().optional()),
  replay_status: z.enum(['hidden', 'public', 'membership_only']).default('hidden'),
  form_template_key: z.preprocess(trimInput, z.string().max(80).default('webinar_free_default')),
  seo_title: webinarOptionalText(70),
  seo_description: webinarOptionalText(170),
  category: webinarOptionalText(100),
  format: webinarOptionalText(100),
  tags: webinarOptionalText(500),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
  sort_order: z.number().int().default(0),
  free_registration_enabled: z.number().int().min(0).max(1).default(1),
  paid_registration_enabled: z.number().int().min(0).max(1).default(1),
  paid_registration_price: z.number().int().min(0).default(99000),
  original_price: z.number().int().min(0).optional().nullable(),
  share_required_count: z.number().int().min(1).default(2),
  share_message_template: z.string().optional().nullable(),
  short_link_enabled: z.number().int().min(0).max(1).default(1),
  redirect_url: webinarOptionalRedirect,
  landing_page_url: webinarOptionalRedirect,
});

export const updateWebinarSchema = createWebinarSchema.partial();

// ─── Webinar Obligation Schemas (admin) ───────────
// Kewajiban pendaftaran gratis yang bisa ditambah bebas oleh admin (follow
// Instagram, subscribe YouTube, share ke N grup, dll) — menggantikan
// checklist share-WA yang dulu fixed/hardcoded. Aditif: webinar tanpa baris
// obligation tetap memakai alur lama (lihat migration 1016).
export const createWebinarObligationSchema = z.object({
  type: z.enum(['share_group', 'follow_instagram', 'subscribe_youtube', 'custom']).default('custom'),
  label: z.string().min(1, 'Label wajib diisi').max(200),
  action_url: z.string().url('URL tidak valid').max(500).optional().nullable().or(z.literal('')),
  requires_proof: z.number().int().min(0).max(1).default(0),
  proof_count_required: z.number().int().min(1).max(10).optional().nullable(),
  sort_order: z.number().int().default(0),
});
export const updateWebinarObligationSchema = createWebinarObligationSchema.partial();

// ─── Webinar Registration Schemas (user) ──────────

// method dikonfirmasi dari domains/webinars/user.routes.ts: handler membanding
// literal 'free_share' | 'paid' saja.
export const webinarSelectMethodSchema = z.object({
  method: z.enum(['free_share', 'paid']),
  click_id: z.string().optional(),
});

export const webinarConfirmFreeShareSchema = z.object({
  confirmed: z.boolean().optional(),
  // Pernyataan bukti asli + siap diblacklist kalau mengelabui — wajib dicentang
  // di UI, divalidasi lagi di server supaya tidak bisa dilewati lewat API langsung.
  integrity_confirmed: z.boolean().optional(),
});

// ─── Order Schemas (admin) ───────────────────────
export const updateOrderStatusSchema = z.object({
  status: z.enum(['pending', 'paid', 'failed', 'cancelled']),
});

// ─── Coupon Schemas ───────────────────────────────
// discount_type dikonfirmasi dari admin/coupons/page.tsx: hanya 'percentage' | 'fixed'.
export const createCouponSchema = z.object({
  code: z.string().min(1, 'Kode kupon wajib diisi').max(60),
  name: z.string().min(1, 'Nama kupon wajib diisi').max(200),
  description: z.string().optional().nullable(),
  discount_type: z.enum(['percentage', 'fixed']),
  discount_value: z.number(),
  max_discount_amount: z.number().optional().nullable(),
  minimum_order_amount: z.number().optional().nullable(),
  starts_at: z.string().optional().nullable(),
  expires_at: z.string().optional().nullable(),
  usage_limit_total: z.number().int().optional().nullable(),
  usage_limit_per_user: z.number().int().optional().nullable(),
  eligible_plans: z.array(z.string()).optional(),
}).passthrough();

// Update juga menerima is_active (absen di create — kupon baru selalu aktif).
export const updateCouponSchema = createCouponSchema.omit({ code: true }).extend({
  is_active: z.union([z.boolean(), z.number()]).optional(),
});

// ─── Public Coupon Schemas ─────────────────────────
// order_type/product_id/plan_id dikoersi longgar (String(v).trim()), meniru
// perilaku lama di public/coupons.routes.ts. Endpoint ini hanya preview;
// perhitungan & validasi final tetap server-side di prepareOrder.
const toTrimmedStringOrUndefined = (v: unknown) =>
  v === undefined || v === null ? undefined : String(v).trim();

export const validateCouponSchema = z.object({
  code: z.preprocess(toTrimmedStringOrUndefined, z.string().min(1, 'Kode kupon wajib diisi')),
  order_type: z.preprocess(toTrimmedStringOrUndefined, z.string().optional()),
  product_id: z.preprocess(toTrimmedStringOrUndefined, z.string().optional()),
  plan_id: z.preprocess(toTrimmedStringOrUndefined, z.string().optional()),
});

// ─── Funnel Schemas ───────────────────────────────
// trigger_product_type/status TIDAK di-enum ketat di sini: nilai sesungguhnya
// (FUNNEL_TRIGGER_TYPES/FUNNEL_STATUSES) hidup di apps/api/domains/funnels/service.ts
// (bukan paket shared), dan route sudah memvalidasinya sendiri saat runtime.
export const createFunnelSchema = z.object({
  name: z.string().min(1, 'Nama funnel wajib diisi'),
  trigger_product_type: z.string().min(1),
  trigger_product_id: z.string().min(1),
  offer_window_minutes: z.number().optional(),
  exit_url: z.string().optional().nullable(),
  // Override manual label trigger — kosong/null = pakai label otomatis dari
  // produk (perilaku default, lihat triggerProductLabel() di funnels.routes.ts).
  trigger_label: z.string().optional().nullable(),
});

export const updateFunnelSchema = z.object({
  name: z.string().min(1).optional(),
  status: z.string().optional(),
  trigger_product_type: z.string().min(1).optional(),
  trigger_product_id: z.string().min(1).optional(),
  offer_window_minutes: z.number().optional(),
  exit_url: z.string().optional().nullable(),
  trigger_label: z.string().optional().nullable(),
});

export const upsertFunnelStepSchema = z.object({
  enabled: z.boolean().optional(),
  product_type: z.string(),
  product_id: z.string(),
  price_override: z.number().optional().nullable(),
  headline: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  badge_text: z.string().optional().nullable(),
  image_url: z.string().optional().nullable(),
  countdown_seconds: z.number().optional().nullable(),
}).passthrough();

// ─── Product Bundle Schemas ───────────────────────
export const createBundleSchema = z.object({
  slug: z.string().min(3).max(80).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash'),
  name: z.string().min(1, 'Nama wajib diisi').max(200),
  description: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  price: z.number().min(0, 'Harga tidak valid (minimal 0)'),
  compare_at_price: z.number().optional().nullable(),
  status: z.enum(['draft', 'active', 'archived']).optional(),
  sort_order: z.number().int().optional(),
  stock_limit: z.number().int().positive().optional().nullable(),
  available_from: z.string().optional().nullable(),
  available_until: z.string().optional().nullable(),
});

export const updateBundleSchema = createBundleSchema.partial();

export const addBundleItemSchema = z.object({
  item_type: z.enum(['digital_product', 'course']),
  item_id: z.string().min(1, 'item_id wajib diisi'),
  sort_order: z.number().int().optional(),
});

// ─── Digital Product Schemas ──────────────────────
export const createDigitalProductSchema = z.object({
  slug: z.string().min(3).max(80).regex(/^[a-z0-9-]+$/, 'Slug hanya huruf kecil, angka, dan dash'),
  name: z.string().min(1, 'Nama wajib diisi').max(200),
  description: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  price: z.number().min(0, 'Harga tidak valid (minimal 0)'),
  compare_at_price: z.number().optional().nullable(),
  delivery_type: z.enum(['files', 'external_url', 'service']).optional(),
  external_url: z.string().optional().nullable(),
  status: z.enum(['draft', 'active', 'archived']).optional(),
  sort_order: z.number().int().optional(),
  pricing_mode: z.enum(['fixed', 'pwyw']).optional(),
  min_price: z.number().int().positive().optional().nullable(),
  stock_limit: z.number().int().positive().optional().nullable(),
  available_from: z.string().optional().nullable(),
  available_until: z.string().optional().nullable(),
});

export const updateDigitalProductSchema = createDigitalProductSchema.partial();

export const createDigitalProductFileSchema = z.object({
  title: z.string().min(1, 'Judul file wajib diisi').max(200),
  file_type: z.string().optional(),
  file_url: z.string().optional().nullable(),
  r2_key: z.string().optional().nullable(),
  sort_order: z.number().int().optional(),
});

// Varian tak punya fase 'draft' — dijual begitu produk induk aktif.
export const createDigitalProductVariantSchema = z.object({
  name: z.string().min(1, 'Nama varian wajib diisi').max(200),
  price: z.number().int().positive('Harga harus lebih dari 0'),
  compare_at_price: z.number().optional().nullable(),
  status: z.enum(['active', 'archived']).optional(),
  sort_order: z.number().int().optional(),
});

export const updateDigitalProductVariantSchema = createDigitalProductVariantSchema.partial();

// ─── Learning Path Schemas ────────────────────────
// level dikonfirmasi dari komentar kolom di migrations/0048_learning_paths.sql;
// status dikonfirmasi dari admin/paths/[id]/page-client.tsx (draft/published/archived).
export const createPathSchema = z.object({
  title: z.string().min(1, 'Judul wajib diisi'),
  slug: z.string().optional(),
  subtitle: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  level: z.enum(['beginner', 'intermediate', 'advanced']).optional().nullable(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
  is_featured: z.union([z.boolean(), z.number()]).optional(),
  sort_order: z.number().int().optional(),
});

export const updatePathSchema = createPathSchema.partial();

export const setPathSkillsSchema = z.object({
  skill_ids: z.array(z.string()).optional(),
});

export const addPathItemSchema = z.object({
  content_id: z.string().min(1, 'content_id wajib diisi'),
  section_label: z.string().optional().nullable(),
  note: z.string().optional().nullable(),
});

export const reorderPathItemsSchema = z.object({
  item_ids: z.array(z.string()).optional(),
});

export const updatePathItemSchema = z.object({
  section_label: z.string().optional().nullable(),
  note: z.string().optional().nullable(),
});

// ─── User Schemas (admin) ─────────────────────────
export const updateUserSchema = z.object({
  name: z.string().optional(),
  email: z.string().optional(),
  role: z.enum(['user', 'admin', 'super_admin']).optional(),
});

export const updateUserStatusSchema = z.object({
  status: z.enum(['active', 'banned', 'suspended']),
});

// Dipakai untuk grant-access DAN revoke-access (bentuk body identik).
export const grantUserAccessSchema = z.object({
  resource_type: z.string().min(1, 'resource_type wajib diisi'),
  resource_id: z.string().min(1, 'resource_id wajib diisi'),
});

// ─── Tag Schemas ──────────────────────────────────
export const createTagSchema = z.object({
  name: z.string().min(1, 'Nama tag wajib diisi'),
});

export const renameTagSchema = z.object({
  name: z.string().min(1, 'Nama tag wajib diisi'),
});

export const mergeTagSchema = z.object({
  target_tag_id: z.string().min(1, 'Target merge tidak valid'),
});

// ─── Skill Schemas ────────────────────────────────
export const createSkillSchema = z.object({
  name: z.string().min(1, 'Nama skill wajib diisi'),
  slug: z.string().optional(),
  id: z.string().optional(),
  description: z.string().optional().nullable(),
  sort_order: z.number().int().optional(),
});

export const updateSkillSchema = z.object({
  slug: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional().nullable(),
  sort_order: z.number().int().optional(),
  is_active: z.union([z.boolean(), z.number()]).optional(),
});

// ─── Webinar Recording Schemas ────────────────────
// Skema ini sebelumnya didefinisikan lokal di admin/recordings.routes.ts;
// dipindah ke sini agar route bisa memakai middleware validate() standar,
// memakai ulang helper preprocess trim/URL yang sama dengan skema webinar.
export const createRecordingSchema = z.object({
  webinar_id: z.preprocess(trimInput, z.string().optional().or(z.literal(''))),
  title: z.preprocess(trimInput, z.string().min(3).max(200)),
  slug: z.preprocess(trimInput, z.string().min(3).max(200).regex(/^[a-z0-9-]+$/)),
  subtitle: webinarOptionalText(300),
  body_html: z.preprocess(trimInput, z.string().optional()),
  thumbnail_url: webinarOptionalUrl,
  recording_url: webinarOptionalUrl,
  recording_embed_url: webinarOptionalUrl,
  category: webinarOptionalText(100),
  format: webinarOptionalText(100),
  tags: webinarOptionalText(500),
  status: z.enum(['hidden', 'public', 'membership_only']).default('hidden'),
  recorded_at: z.preprocess(trimInput, z.string().min(1)),
  sort_order: z.number().int().default(0),
});

export const updateRecordingSchema = createRecordingSchema.partial();

// ─── Content Registry Schemas (admin) ─────────────
export const setContentSkillsSchema = z.object({
  skill_ids: z.array(z.string()).optional(),
  primary_skill_id: z.string().optional().nullable(),
});

export const setContentTagsSchema = z.object({
  tag_names: z.array(z.string()).optional(),
});

// ─── Challenge Schemas ────────────────────────────
export const createChallengeSchema = z.object({
  title: z.string().min(1, 'Judul wajib diisi'),
  description: z.string().optional().nullable(),
  metric: z.enum(['lessons', 'points', 'referrals', 'webinars'], {
    errorMap: () => ({ message: 'Metric tidak valid (lessons | points | referrals | webinars)' }),
  }),
  target: z.number().positive('Target harus bilangan > 0'),
  reward_label: z.string().optional().nullable(),
  starts_at: z.string().refine((v) => !Number.isNaN(new Date(v).getTime()), 'Tanggal mulai tidak valid'),
  ends_at: z.string().refine((v) => !Number.isNaN(new Date(v).getTime()), 'Tanggal berakhir tidak valid'),
  status: z.enum(['draft', 'active', 'ended']).optional(),
}).refine((data) => new Date(data.ends_at) > new Date(data.starts_at), {
  message: 'Tanggal berakhir harus setelah mulai',
  path: ['ends_at'],
});

// ─── Resource Tag Schemas ─────────────────────────
export const setResourceTagsSchema = z.object({
  resource_type: z.string().min(1, 'resource_type wajib diisi'),
  tags: z.array(z.object({
    tag_type: z.string(),
    tag_value: z.string(),
  })),
});

// ─── Push Notification Schemas ────────────────────
export const pushBroadcastSchema = z.object({
  title: z.string().min(1, 'Judul wajib diisi'),
  body: z.string().min(1, 'Isi wajib diisi'),
  url: z.string().optional(),
  segment: z.enum(['all', 'paid', 'free']).optional(),
});

// User: bentuk PushSubscription browser (endpoint + keys.p256dh/keys.auth) —
// dikonfirmasi dari domains/user/push.routes.ts.
export const pushSubscribeSchema = z.object({
  endpoint: z.string().min(1, 'Subscription tidak valid'),
  keys: z.object({
    p256dh: z.string().min(1, 'Subscription tidak valid'),
    auth: z.string().min(1, 'Subscription tidak valid'),
  }),
});

export const pushUnsubscribeSchema = z.object({
  endpoint: z.string().min(1, 'Endpoint wajib diisi'),
});

// ─── Community Schemas (admin) ────────────────────
export const updateCommunityPostSchema = z.object({
  title: z.string().optional(),
  body_html: z.string().optional(),
  body_text: z.string().optional(),
});

// ─── Commission Schemas (admin) ───────────────────
export const rejectCommissionSchema = z.object({
  notes: z.string().optional().nullable(),
});

// ─── Certificate Schemas (admin) ──────────────────
export const issueCertificateSchema = z.object({
  user_id: z.string().min(1, 'User ID and Course ID required'),
  course_id: z.string().min(1, 'User ID and Course ID required'),
});

// ─── Certificate Schemas (user) ───────────────────
export const generateCertificateSchema = z.object({
  course_id: z.string().min(1, 'Course ID required'),
});

// ─── Analytics API Key Schemas ────────────────────
// scopes/webhook_url/allowed_origins sengaja z.unknown(): normalizeScopes /
// validateWebhookUrl / normalizeOrigins (apps/api/lib/api-key-validation.ts)
// sudah melakukan validasi bentuk & isinya sendiri dari raw value.
export const createAnalyticsKeySchema = z.object({
  name: z.string().trim().min(1, 'name is required'),
  expires_at: z.string().optional().nullable(),
  scopes: z.unknown().optional(),
  webhook_url: z.unknown().optional(),
  allowed_origins: z.unknown().optional(),
});

// ─── AI Fill Schemas (admin) ──────────────────────
export const aiFillSchema = z.object({
  mode: z.enum(['field', 'bundle']).optional(),
  entity_type: z.enum(['course', 'lesson', 'webinar', 'email_campaign'], {
    errorMap: () => ({ message: 'entity_type tidak valid' }),
  }),
  field: z.string().optional(),
  fields: z.array(z.string()).optional(),
  context: z.unknown().optional(),
  prompt: z.string().min(1, 'Prompt wajib diisi'),
  output_format: z.enum(['plain', 'html']).optional(),
});

// ─── Affiliate Schemas (admin) ────────────────────
// status dikonfirmasi dari admin/affiliates/page.tsx (toggle active <-> inactive).
export const updateAffiliateSchema = z.object({
  status: z.enum(['active', 'inactive']).optional(),
  default_commission_rate: z.number().optional(),
});

// ─── Email Schemas (admin) ────────────────────────
export const updateEmailTemplateStepSchema = z.object({
  subject: z.string().min(1, 'Subject wajib diisi'),
  previewText: z.string().optional().nullable(),
  ctaUrl: z.string().optional().nullable(),
  content: z.string().optional().nullable(),
  delayMinutes: z.number().optional().nullable(),
  contentFormat: z.enum(['html', 'blocks']).optional(),
  isActive: z.boolean().optional(),
});

export const previewEmailTemplateSchema = z.object({
  content: z.string().min(1, 'Konten kosong'),
  contentFormat: z.enum(['html', 'blocks']).optional(),
  ctaUrl: z.string().optional(),
  previewText: z.string().optional(),
});

export const reorderEmailStepsSchema = z.object({
  stepIds: z.array(z.string()).min(1, 'stepIds wajib berupa array'),
});

// triggerEvent TIDAK di-enum di sini terhadap SEQUENCE_CUSTOM_TRIGGER_EVENTS —
// route sudah memvalidasinya sendiri terhadap daftar itu setelah schema lolos.
export const createEmailSequenceSchema = z.object({
  name: z.string().min(1, 'Nama sequence wajib diisi'),
  triggerEvent: z.string().min(1),
});

// Dipakai untuk create (POST /campaigns) dan update (PUT /campaigns/:id) — bentuk
// body identik, kedua endpoint mewajibkan ketiga field yang sama.
export const emailCampaignSchema = z.object({
  name: z.string().min(1, 'Nama, subject, dan konten wajib diisi'),
  subject: z.string().min(1, 'Nama, subject, dan konten wajib diisi'),
  content: z.string().min(1, 'Nama, subject, dan konten wajib diisi'),
  segment: z.string().optional(),
});

export const sendTestEmailSchema = z.object({
  to: z.string().min(1, 'Email tujuan, subject, dan konten wajib diisi'),
  subject: z.string().min(1, 'Email tujuan, subject, dan konten wajib diisi'),
  html: z.string().min(1, 'Email tujuan, subject, dan konten wajib diisi'),
});

export const updateSequenceStatusSchema = z.object({
  status: z.enum(['active', 'paused'], {
    errorMap: () => ({ message: 'Status harus "active" atau "paused"' }),
  }),
});

export const activateEmailMarketingSchema = z.object({
  preset: z.string().min(1),
  sequences: z.array(z.string()).optional(),
});

export const setEmailSenderDomainSchema = z.object({
  domain: z.string().min(1, 'domain wajib diisi'),
  from_local: z.string().optional(),
  sender_name: z.string().optional(),
});

// ─── Settings Schemas ────────────────────────────
// Bentuk asli PUT /api/admin/settings/:key: key datang dari URL param, body
// cuma { value }, dan `value` sengaja bebas tipe (string/number/boolean/objek/
// array/null) — app_settings menyimpannya sebagai JSON generik apa adanya.
// (Skema lama {key,value_json} tak pernah cocok dengan route manapun.)
export const updateSettingSchema = z.object({
  value: z.unknown(),
}).refine((v) => v.value !== undefined, { message: 'value is required', path: ['value'] });

// ─── Exam/Tryout Schemas (admin) ──────────────────
// status dikonfirmasi dari admin/tryout/_components/BankSoalTab.tsx (draft|active|archived).
export const createQuestionBankSchema = z.object({
  title: z.string().min(1, 'title is required'),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  status: z.enum(['draft', 'active', 'archived']).optional(),
});

export const updateQuestionBankSchema = createQuestionBankSchema.partial();

// Wrapper array; tiap baris divalidasi ulang per-baris di handler lewat
// examImportRowSchema (exam-import.ts) — di sini cukup pastikan bentuk {rows: [...]}.
export const importQuestionBankSchema = z.object({
  rows: z.array(z.unknown()).min(1, 'rows kosong').max(500, 'Maksimal 500 baris per import'),
});

// question_type dikonfirmasi dari admin.routes.ts (validateOptions/include-check):
// single_choice | multi_choice | true_false | short_answer.
const questionOptionInputSchema = z.object({
  label: z.string().optional(),
  body_html: z.string().optional(),
  is_correct: z.union([z.boolean(), z.number()]).optional(),
  sort_order: z.number().int().optional(),
});

export const createQuestionSchema = z.object({
  bank_id: z.string().min(1, 'bank_id dan body_html wajib diisi'),
  question_type: z.enum(['single_choice', 'multi_choice', 'true_false', 'short_answer']).optional(),
  body_html: z.string().min(1, 'bank_id dan body_html wajib diisi'),
  explanation_html: z.string().optional().nullable(),
  topic: z.string().optional().nullable(),
  difficulty: z.number().optional(),
  points: z.number().optional(),
  audio_r2_key: z.string().optional().nullable(),
  image_r2_key: z.string().optional().nullable(),
  // Tidak ada UI editor untuk status soal — biarkan permisif (z.string()).
  status: z.string().optional(),
  options: z.array(questionOptionInputSchema).optional(),
});

export const updateQuestionSchema = createQuestionSchema.partial().omit({ bank_id: true });

// exam_type/access_type dikonfirmasi dari ExamBuilder.tsx (admin/tryout);
// show_explanation dikonfirmasi dari service.ts (tipe union literal).
export const createExamSchema = z.object({
  slug: z.string().min(1, 'slug dan title wajib diisi'),
  title: z.string().min(1, 'slug dan title wajib diisi'),
  description: z.string().optional().nullable(),
  exam_type: z.enum(['tryout', 'quiz', 'pretest', 'posttest']).optional(),
  course_id: z.string().optional().nullable(),
  access_type: z.enum(['free', 'membership_only', 'purchase']).optional(),
  duration_minutes: z.number().int().optional().nullable(),
  passing_score: z.number().optional().nullable(),
  max_attempts: z.number().int().optional().nullable(),
  shuffle_questions: z.union([z.boolean(), z.number()]).optional(),
  show_explanation: z.enum(['never', 'after_submit', 'after_close']).optional(),
});

// PUT /exams/:id hanya mengizinkan field di EXAM_FIELDS (sama persis dengan
// createExamSchema tanpa status) — .partial() aman karena field ber-default
// tidak ada di sini (semua optional murni).
export const updateExamSchema = createExamSchema.partial();

export const createExamSectionSchema = z.object({
  title: z.string().min(1, 'title is required'),
  duration_minutes: z.number().int().optional().nullable(),
  sort_order: z.number().int().optional(),
});

export const updateExamSectionSchema = createExamSectionSchema.partial();

export const setExamQuestionsSchema = z.object({
  items: z.array(z.object({
    question_id: z.string().min(1, 'Setiap item butuh question_id'),
    section_id: z.string().optional().nullable(),
    sort_order: z.number().int().optional(),
    points_override: z.number().optional().nullable(),
  })),
});

// ─── Exam Answer Schema (member — routes.ts) ──────
export const upsertExamAnswerSchema = z.object({
  question_id: z.string().min(1, 'question_id wajib diisi'),
  selected_option_ids: z.array(z.string()).optional().nullable(),
  answer_text: z.string().optional().nullable(),
});

// ─── Webhook Schemas (Mailketing) ─────────────────
// Payload bentuknya didikte penyedia eksternal (Mailketing/Mail Hub) — bukan
// kita yang menentukan. Cukup pastikan body berupa objek; tiap field dibaca
// defensif via String(body.xxx || '') di processor (webhooks/mailketing.routes.ts),
// jadi sengaja longgar, bukan menemukan aturan bisnis baru.
export const mailketingWebhookSchema = z.object({}).passthrough();

// ─── Platform Merchant Schemas (admin — platform/merchants.routes.ts) ──
// Sisi platform super-admin meninjau merchant PSP (approve/suspend/reject/
// payout) — beda dari Merchant Portal Schemas di bawah (self-service PSP,
// domain lain). Longgar (bukan .strict()) mengikuti konvensi admin/platform.
export const platformMerchantApproveSchema = z.object({
  fee_rate_bps: z.number().int().min(0).max(2000),
  fee_flat_idr: z.number().int().min(0).max(100_000).default(0),
  max_charge_amount: z.number().int().positive().optional(),
  daily_charge_limit: z.number().int().positive().optional(),
});

export const platformMerchantSuspendSchema = z.object({
  reason: z.string().trim().min(1, 'Alasan penangguhan wajib diisi'),
});

export const platformMerchantRejectSchema = z.object({
  reason: z.string().trim().optional(),
});

export const platformMerchantPayoutMarkPaidSchema = z.object({
  transfer_ref: z.string().trim().min(1, 'Bukti transfer (transfer_ref) wajib diisi'),
});

// ─── Platform Tenant Schemas (admin — platform/tenants.routes.ts) ──────
// createTenantSchema/patchTenantSchema/requestDomainSchema TETAP lokal di
// tenants.routes.ts: butuh TENANT_SLUG_RE/RESERVED_SLUGS dari
// provisioning.service.ts (apps/api lokal, tak bisa diimpor ke paket shared).
// Tiga endpoint di bawah tak punya dependensi itu, jadi ikut pola umum.
export const tenantBackfillEmailDefaultsSchema = z.object({
  confirm: z.boolean().optional(),
});

export const tenantDeleteSchema = z.object({
  confirm: z.string().optional(),
});

export const tenantResendWelcomeSchema = z.object({
  confirm: z.boolean().optional(),
  include_inactive: z.boolean().optional(),
});

// Atribusi akuisisi opsional (UTM generik + ref affiliate growclass) — dibaca
// dari cookie yang ditangkap apps/landing (UtmBridge), diteruskan apa adanya
// ke provisioning.service.ts untuk ditulis SEKALI ke tenants.acquisition_*/
// referred_by_* (lihat plan "Watermark UTM growth-data + Program Affiliate
// Growclass"). Semua field optional — signup tanpa UTM sama sekali tetap valid.
export const signupAttributionSchema = z.object({
  utm_source: z.string().max(120).optional(),
  utm_medium: z.string().max(120).optional(),
  utm_campaign: z.string().max(120).optional(),
  utm_content: z.string().max(120).optional(),
  utm_term: z.string().max(120).optional(),
  ref: z.string().max(64).optional(),
});
export type SignupAttribution = z.infer<typeof signupAttributionSchema>;

// ─── Badge klik "Powered by Growclass" (public, tanpa auth — badge-visit.routes.ts) ───
// Dipanggil apps/landing saat mendeteksi utm_source=growclass_badge di URL.
// utm_content = slug tenant (utk resolve tenant_id); ref = kode affiliate bila ada.
export const badgeVisitSchema = z.object({
  utm_content: z.string().max(120).optional(),
  utm_term: z.enum(['admin', 'public']).optional(),
  ref: z.string().max(64).optional(),
});
export type BadgeVisitInput = z.infer<typeof badgeVisitSchema>;

// ─── Platform Signup Schema (public self-serve — signup.routes.ts) ────
/**
 * Opsi "Tahu Growclass dari mana?" di form signup platform.
 *
 * Melengkapi acquisition_utm_* yang terisi otomatis dari URL: UTM kosong untuk
 * pengunjung yang mengetik langsung, datang dari obrolan WhatsApp, atau dari
 * rekomendasi mulut ke mulut — dan justru itu kanal yang tidak terlihat sama
 * sekali di data sekarang.
 */
export const SIGNUP_REFERRAL_SOURCES = [
  'Instagram',
  'TikTok',
  'YouTube',
  'Google / Pencarian',
  'LinkedIn',
  'Dari teman / rekomendasi',
  'Komunitas WhatsApp / Telegram',
  'Iklan',
  'Lainnya',
] as const;

export const platformSignupSchema = z.object({
  email: z.string().email().max(160),
  password: z.string().min(8).max(200),
  brand_name: z.string().trim().min(2).max(80),
  slug: z.string().min(3).max(30),
  business_type: z.string().max(40).optional(),
  admin_name: z.string().trim().max(80).optional(),
  // WAJIB. Bukan enum ketat supaya daftar opsi bisa diubah tanpa deploy API
  // lebih dulu — tapi tetap dibatasi panjangnya.
  referral_source: z.string().trim().min(1, 'Pilih dari mana kamu tahu Growclass').max(60),
  cfTurnstileResponse: z.string().optional(),
  _hp_website: z.string().optional(), // honeypot: harus kosong
  attribution: signupAttributionSchema.optional(),
});

// ─── Platform Find-Subdomain Schema (public — find-subdomain.routes.ts) ─
// email TETAP wajib format valid via zod; anti-enumeration-nya ada di level
// RESPONS (handler SELALU balas 200 generik terlepas hasil parse), bukan di
// level skema — jangan ubah endpoint ini ke validate() middleware otomatis
// (itu akan membalas 400 saat parse gagal, membocorkan sinyal beda status).
export const findSubdomainSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(160),
  cfTurnstileResponse: z.string().optional(),
  _hp_website: z.string().optional(), // honeypot: harus kosong
});

// ─── Merchant Portal Schemas (PSP — portal.routes.ts) ──────
// Dipindah dari skema lokal di portal.routes.ts supaya konsisten dengan
// pola validate() + shared schemas di seluruh codebase. .strict() dipertahankan
// (bukan ditambah baru) persis seperti versi lokal sebelumnya — endpoint ini
// menyentuh kredensial/uang jadi extra key ditolak eksplisit.
export const merchantRegisterSchema = z.object({
  business_name: z.string().min(2).max(120),
  email: z.string().email().max(254),
  password: z.string().min(8).max(200),
  phone: z.string().max(24).optional(),
  legal_name: z.string().max(200).optional(),
  _hp_website: z.string().optional(),
}).strict();

export const merchantLoginSchema = z.object({
  email: z.string().min(1, 'Email dan password wajib diisi'),
  password: z.string().min(1, 'Email dan password wajib diisi'),
});

// email sengaja optional & tanpa format .email(): handler selalu balas 200
// generik (anti-enumerasi) walau email kosong/tidak valid — jangan diubah
// jadi wajib/berformat, itu akan membocorkan status via kode 400.
export const merchantForgotPasswordSchema = z.object({
  email: z.string().optional(),
});

export const merchantResetPasswordSchema = z.object({
  token: z.string().min(1, 'Token dan password minimal 8 karakter wajib diisi'),
  password: z.string().min(8, 'Token dan password minimal 8 karakter wajib diisi'),
});

export const merchantAcceptInviteSchema = z.object({
  token: z.string().min(1, 'Token dan password minimal 8 karakter wajib diisi'),
  password: z.string().min(8, 'Token dan password minimal 8 karakter wajib diisi'),
  name: z.string().optional(),
});

export const merchantPayoutAccountSchema = z.object({
  bank_name: z.string().min(2).max(60),
  bank_account: z.string().regex(/^[0-9]{4,30}$/, 'Nomor rekening hanya angka (4–30 digit)'),
  account_holder: z.string().min(2).max(120),
}).strict();

export const merchantKycDocumentSchema = z.object({
  doc_type: z.enum(['ktp', 'npwp', 'nib', 'bank_statement']),
  r2_key: z.string().min(1).max(500),
}).strict();

export const merchantInviteUserSchema = z.object({
  email: z.string().email().max(254),
  role: z.enum(['owner', 'staff']).default('staff'),
}).strict();

// webhook_url/allowed_origins sengaja z.unknown(): validateWebhookUrl/
// normalizeOrigins (lib/api-key-validation.ts) sudah memvalidasi bentuk &
// isinya sendiri dari raw value (sama seperti createAnalyticsKeySchema).
export const createMerchantApiKeySchema = z.object({
  name: z.string().trim().min(1, 'Nama key wajib diisi'),
  expires_at: z.string().optional(),
  webhook_url: z.unknown().optional(),
  allowed_origins: z.unknown().optional(),
});

// ─── Affiliate Schemas (student-facing — affiliate/routes.ts) ─────
// Note: berbeda dari updateAffiliateSchema di atas (admin CRUD) — ini endpoint
// student-facing (track click publik + payout settings) di domains/affiliate/routes.ts.
export const trackAffiliateClickSchema = z.object({
  affiliate_code: z.string().min(1, 'affiliate_code required'),
  landing_url: z.string().optional(),
  referrer_url: z.string().optional(),
  campaign: z.string().optional(),
});

export const updateAffiliatePayoutSchema = z.object({
  payout_name: z.string().optional(),
  payout_bank_name: z.string().optional(),
  payout_bank_account: z.string().optional(),
});

// ─── Automation Schemas (automations/routes.ts) ────────────────────
// schedule/credentials sengaja longgar (z.unknown()/z.record) — bentuknya union
// bertingkat (lihat AutomationConfig/Schedule di domains/automations/types.ts)
// dan sudah divalidasi ulang oleh AutomationService (mis. schedule wajib ada
// untuk create, template dicek terhadap TEMPLATES).
export const automationConfigSchema = z.object({
  schedule: z.unknown().optional(),
  credentials: z.record(z.string()).optional(),
  systemPromptOverride: z.string().optional(),
  replizAccountId: z.string().optional(),
  replizUserId: z.string().optional(),
  telegramChatId: z.string().optional(),
}).passthrough();

export const createAutomationCredentialSchema = z.object({
  type: z.string().optional(),
  label: z.string().optional(),
  fields: z.record(z.string()).optional(),
});

export const createAutomationSchema = z.object({
  templateKey: z.string().optional(),
  name: z.string().optional(),
  config: automationConfigSchema.optional(),
  topics: z.array(z.string()).optional(),
});

export const updateAutomationSchema = z.object({
  name: z.string().optional(),
  config: automationConfigSchema.optional(),
});

// status dikonfirmasi dari AutomationStatus (domains/automations/types.ts) dan
// pemeriksaan literal di service.setStatus.
export const setAutomationStatusSchema = z.object({
  status: z.enum(['active', 'paused', 'draft'], {
    errorMap: () => ({ message: 'Status tidak valid' }),
  }),
});

export const addAutomationTopicsSchema = z.object({
  topics: z.array(z.string()).optional(),
});

// ─── Community Comment Schema (community/routes.ts) ────────────────
// Note: berbeda dari domains/admin/community.routes.ts (moderasi post admin,
// sudah ditangani batch sebelumnya) — ini komentar member di domains/community/routes.ts.
export const createCommunityCommentSchema = z.object({
  body_text: z.string().min(1, 'Komentar wajib diisi'),
});

// ─── Email Send/Subscribe Schemas (email/routes.ts) ─────────────────
// Note: berbeda dari domains/admin/email.routes.ts (campaign/sequence admin,
// sudah ditangani batch sebelumnya) — ini endpoint send/subscribe/trigger langsung
// di domains/email/routes.ts. emailType dikonfirmasi dari services/mailketing.ts.
export const sendEmailSchema = z.object({
  recipient: z.string().min(1, 'recipient, subject, and content are required'),
  subject: z.string().min(1, 'recipient, subject, and content are required'),
  content: z.string().min(1, 'recipient, subject, and content are required'),
  emailType: z.enum(['transactional', 'marketing', 'lifecycle', 'notification']).optional(),
  campaignId: z.string().optional(),
  sequenceId: z.string().optional(),
});

export const subscribeEmailSchema = z.object({
  email: z.string().min(1, 'email and listId are required'),
  listId: z.string().min(1, 'email and listId are required'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.string().optional(),
  interest: z.string().optional(),
  source: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
});

export const triggerEmailSequenceSchema = z.object({
  userId: z.string().min(1, 'userId and sequenceName are required'),
  sequenceName: z.string().min(1, 'userId and sequenceName are required'),
});

// ─── Funnel Offer Schemas (funnels/routes.ts) ───────────────────────
// Note: berbeda dari domains/admin/funnels.routes.ts (CRUD funnel admin, sudah
// ditangani batch sebelumnya) — ini endpoint accept/decline yang dipakai
// pembeli di domains/funnels/routes.ts. step_type dikonfirmasi dari
// FunnelsService.acceptOffer/declineOffer (menolak selain 'upsell'/'downsell').
export const acceptFunnelOfferSchema = z.object({
  step_type: z.enum(['upsell', 'downsell'], {
    errorMap: () => ({ message: 'step_type tidak valid' }),
  }),
  payment_method: z.string().optional(),
});

export const declineFunnelOfferSchema = z.object({
  step_type: z.enum(['upsell', 'downsell'], {
    errorMap: () => ({ message: 'step_type tidak valid' }),
  }),
});

// ─── Invoice Schemas (invoices/routes.ts) ───────────────────────────
const invoiceItemInputSchema = z.object({
  item_name: z.string().min(1, 'item_name wajib diisi'),
  description: z.string().nullable().optional(),
  quantity: z.number().optional(),
  unit_price: z.number().optional(),
});

export const invoiceInputSchema = z.object({
  company_name: z.string().min(1, 'Nama perusahaan, nama penerima, dan email wajib diisi'),
  recipient_name: z.string().min(1, 'Nama perusahaan, nama penerima, dan email wajib diisi'),
  recipient_email: z.string().min(1, 'Nama perusahaan, nama penerima, dan email wajib diisi'),
  recipient_phone: z.string().optional(),
  company_address: z.string().optional(),
  tax_number: z.string().optional(),
  invoice_date: z.string().optional(),
  due_date: z.string().optional(),
  currency: z.string().optional(),
  discount: z.number().optional(),
  tax: z.number().optional(),
  payment_method: z.string().optional(),
  payment_information: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(invoiceItemInputSchema).min(1, 'Minimal satu item invoice diperlukan'),
});

// status dikonfirmasi dari daftar `allowed` di PATCH /:id/status.
export const updateInvoiceStatusSchema = z.object({
  status: z.enum(['draft', 'generated', 'sent', 'paid', 'cancelled', 'overdue'], {
    errorMap: () => ({ message: 'Status tidak valid' }),
  }),
});

// ─── Shortlink Schemas (shortlinks/routes.ts) ───────────────────────
export const createShortlinkSchema = z.object({
  slug: z.string().optional(),
  destination_url: z.string().min(1, 'destination_url wajib diisi'),
  title: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  promo_location: z.string().optional(),
});

export const updateShortlinkSchema = z.object({
  title: z.string().nullable().optional(),
  destination_url: z.string().optional(),
  is_active: z.boolean().optional(),
  utm_source: z.string().nullable().optional(),
  utm_medium: z.string().nullable().optional(),
  utm_campaign: z.string().nullable().optional(),
  promo_location: z.string().nullable().optional(),
});

// ─── User Learning/Onboarding Schemas (user/routes.ts) ──────────────
export const enrollFreeCourseSchema = z.object({
  course_id: z.string().min(1, 'Course ID is required'),
});

export const progressCompleteSchema = z.object({
  enrollment_id: z.string().min(1, 'Missing required fields'),
  lesson_id: z.string().min(1, 'Missing required fields'),
  is_completed: z.boolean().optional(),
  score: z.number().optional(),
  max_score: z.number().optional(),
});

export const progressWatchSchema = z.object({
  enrollment_id: z.string().min(1, 'Missing required fields'),
  lesson_id: z.string().min(1, 'Missing required fields'),
  position_seconds: z.number().optional(),
  duration_seconds: z.number().optional(),
  milestone: z.number().optional(),
});

// role/primary_goal/ai_level/recommended_track TIDAK di-enum: nilainya
// dikonfigurasi admin lewat panel Onboarding (website config), bukan konstanta
// tetap di kode (lihat admin/website/_components/OnboardingTab.tsx).
export const userOnboardingSchema = z.object({
  role: z.string().optional(),
  primary_goal: z.string().optional(),
  ai_level: z.string().optional(),
  interests: z.array(z.string()).optional(),
  recommended_track: z.string().optional(),
});

// Type exports
export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateCourseInput = z.infer<typeof createCourseSchema>;
export type UpdateCourseInput = z.infer<typeof updateCourseSchema>;
export type CreateModuleInput = z.infer<typeof createModuleSchema>;
export type CreateLessonInput = z.infer<typeof createLessonSchema>;
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type CreateMembershipPlanInput = z.infer<typeof createMembershipPlanSchema>;
export type UpdateMembershipPlanInput = z.infer<typeof updateMembershipPlanSchema>;
export type GrantMembershipInput = z.infer<typeof grantMembershipSchema>;
export type MembershipDaysInput = z.infer<typeof membershipDaysSchema>;
export type CreateBonusInput = z.infer<typeof createBonusSchema>;
export type UpdateBonusInput = z.infer<typeof updateBonusSchema>;
export type CreateBonusFileInput = z.infer<typeof createBonusFileSchema>;
export type CreateWebinarInput = z.infer<typeof createWebinarSchema>;
export type UpdateWebinarInput = z.infer<typeof updateWebinarSchema>;
export type CreateWebinarObligationInput = z.infer<typeof createWebinarObligationSchema>;
export type UpdateWebinarObligationInput = z.infer<typeof updateWebinarObligationSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type RecordMilestoneInput = z.infer<typeof recordMilestoneSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ReorderLessonsInput = z.infer<typeof reorderLessonsSchema>;
export type UpdateSettingInput = z.infer<typeof updateSettingSchema>;
export type UpdateOrderStatusInput = z.infer<typeof updateOrderStatusSchema>;
export type CreateCouponInput = z.infer<typeof createCouponSchema>;
export type UpdateCouponInput = z.infer<typeof updateCouponSchema>;
export type CreateFunnelInput = z.infer<typeof createFunnelSchema>;
export type UpdateFunnelInput = z.infer<typeof updateFunnelSchema>;
export type UpsertFunnelStepInput = z.infer<typeof upsertFunnelStepSchema>;
export type CreateBundleInput = z.infer<typeof createBundleSchema>;
export type UpdateBundleInput = z.infer<typeof updateBundleSchema>;
export type AddBundleItemInput = z.infer<typeof addBundleItemSchema>;
export type CreateDigitalProductInput = z.infer<typeof createDigitalProductSchema>;
export type UpdateDigitalProductInput = z.infer<typeof updateDigitalProductSchema>;
export type CreateDigitalProductFileInput = z.infer<typeof createDigitalProductFileSchema>;
export type CreateDigitalProductVariantInput = z.infer<typeof createDigitalProductVariantSchema>;
export type UpdateDigitalProductVariantInput = z.infer<typeof updateDigitalProductVariantSchema>;
export type CreatePathInput = z.infer<typeof createPathSchema>;
export type UpdatePathInput = z.infer<typeof updatePathSchema>;
export type SetPathSkillsInput = z.infer<typeof setPathSkillsSchema>;
export type AddPathItemInput = z.infer<typeof addPathItemSchema>;
export type ReorderPathItemsInput = z.infer<typeof reorderPathItemsSchema>;
export type UpdatePathItemInput = z.infer<typeof updatePathItemSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type UpdateUserStatusInput = z.infer<typeof updateUserStatusSchema>;
export type GrantUserAccessInput = z.infer<typeof grantUserAccessSchema>;
export type CreateTagInput = z.infer<typeof createTagSchema>;
export type RenameTagInput = z.infer<typeof renameTagSchema>;
export type MergeTagInput = z.infer<typeof mergeTagSchema>;
export type CreateSkillInput = z.infer<typeof createSkillSchema>;
export type UpdateSkillInput = z.infer<typeof updateSkillSchema>;
export type CreateRecordingInput = z.infer<typeof createRecordingSchema>;
export type UpdateRecordingInput = z.infer<typeof updateRecordingSchema>;
export type SetContentSkillsInput = z.infer<typeof setContentSkillsSchema>;
export type SetContentTagsInput = z.infer<typeof setContentTagsSchema>;
export type CreateChallengeInput = z.infer<typeof createChallengeSchema>;
export type SetResourceTagsInput = z.infer<typeof setResourceTagsSchema>;
export type PushBroadcastInput = z.infer<typeof pushBroadcastSchema>;
export type UpdateCommunityPostInput = z.infer<typeof updateCommunityPostSchema>;
export type RejectCommissionInput = z.infer<typeof rejectCommissionSchema>;
export type IssueCertificateInput = z.infer<typeof issueCertificateSchema>;
export type CreateAnalyticsKeyInput = z.infer<typeof createAnalyticsKeySchema>;
export type AiFillInput = z.infer<typeof aiFillSchema>;
export type UpdateAffiliateInput = z.infer<typeof updateAffiliateSchema>;
export type UpdateEmailTemplateStepInput = z.infer<typeof updateEmailTemplateStepSchema>;
export type PreviewEmailTemplateInput = z.infer<typeof previewEmailTemplateSchema>;
export type ReorderEmailStepsInput = z.infer<typeof reorderEmailStepsSchema>;
export type CreateEmailSequenceInput = z.infer<typeof createEmailSequenceSchema>;
export type EmailCampaignInput = z.infer<typeof emailCampaignSchema>;
export type SendTestEmailInput = z.infer<typeof sendTestEmailSchema>;
export type UpdateSequenceStatusInput = z.infer<typeof updateSequenceStatusSchema>;
export type ActivateEmailMarketingInput = z.infer<typeof activateEmailMarketingSchema>;
export type SetEmailSenderDomainInput = z.infer<typeof setEmailSenderDomainSchema>;
export type CreateQuestionBankInput = z.infer<typeof createQuestionBankSchema>;
export type UpdateQuestionBankInput = z.infer<typeof updateQuestionBankSchema>;
export type ImportQuestionBankInput = z.infer<typeof importQuestionBankSchema>;
export type CreateQuestionInput = z.infer<typeof createQuestionSchema>;
export type UpdateQuestionInput = z.infer<typeof updateQuestionSchema>;
export type CreateExamInput = z.infer<typeof createExamSchema>;
export type UpdateExamInput = z.infer<typeof updateExamSchema>;
export type CreateExamSectionInput = z.infer<typeof createExamSectionSchema>;
export type UpdateExamSectionInput = z.infer<typeof updateExamSectionSchema>;
export type SetExamQuestionsInput = z.infer<typeof setExamQuestionsSchema>;
export type UpsertExamAnswerInput = z.infer<typeof upsertExamAnswerSchema>;
export type MerchantRegisterInput = z.infer<typeof merchantRegisterSchema>;
export type MerchantLoginInput = z.infer<typeof merchantLoginSchema>;
export type MerchantForgotPasswordInput = z.infer<typeof merchantForgotPasswordSchema>;
export type MerchantResetPasswordInput = z.infer<typeof merchantResetPasswordSchema>;
export type MerchantAcceptInviteInput = z.infer<typeof merchantAcceptInviteSchema>;
export type MerchantPayoutAccountInput = z.infer<typeof merchantPayoutAccountSchema>;
export type MerchantKycDocumentInput = z.infer<typeof merchantKycDocumentSchema>;
export type MerchantInviteUserInput = z.infer<typeof merchantInviteUserSchema>;
export type CreateMerchantApiKeyInput = z.infer<typeof createMerchantApiKeySchema>;
export type WebinarSelectMethodInput = z.infer<typeof webinarSelectMethodSchema>;
export type WebinarConfirmFreeShareInput = z.infer<typeof webinarConfirmFreeShareSchema>;
export type ValidateCouponInput = z.infer<typeof validateCouponSchema>;
export type PushSubscribeInput = z.infer<typeof pushSubscribeSchema>;
export type PushUnsubscribeInput = z.infer<typeof pushUnsubscribeSchema>;
export type GenerateCertificateInput = z.infer<typeof generateCertificateSchema>;
export type MailketingWebhookInput = z.infer<typeof mailketingWebhookSchema>;
export type PlatformMerchantApproveInput = z.infer<typeof platformMerchantApproveSchema>;
export type PlatformMerchantSuspendInput = z.infer<typeof platformMerchantSuspendSchema>;
export type PlatformMerchantRejectInput = z.infer<typeof platformMerchantRejectSchema>;
export type PlatformMerchantPayoutMarkPaidInput = z.infer<typeof platformMerchantPayoutMarkPaidSchema>;
export type TenantBackfillEmailDefaultsInput = z.infer<typeof tenantBackfillEmailDefaultsSchema>;
export type TenantDeleteInput = z.infer<typeof tenantDeleteSchema>;
export type TenantResendWelcomeInput = z.infer<typeof tenantResendWelcomeSchema>;
export type PlatformSignupInput = z.infer<typeof platformSignupSchema>;
export type FindSubdomainInput = z.infer<typeof findSubdomainSchema>;
export type TrackAffiliateClickInput = z.infer<typeof trackAffiliateClickSchema>;
export type UpdateAffiliatePayoutInput = z.infer<typeof updateAffiliatePayoutSchema>;
export type AutomationConfigInput = z.infer<typeof automationConfigSchema>;
export type CreateAutomationCredentialInput = z.infer<typeof createAutomationCredentialSchema>;
export type CreateAutomationInput = z.infer<typeof createAutomationSchema>;
export type UpdateAutomationInput = z.infer<typeof updateAutomationSchema>;
export type SetAutomationStatusInput = z.infer<typeof setAutomationStatusSchema>;
export type AddAutomationTopicsInput = z.infer<typeof addAutomationTopicsSchema>;
export type CreateCommunityCommentInput = z.infer<typeof createCommunityCommentSchema>;
export type SendEmailInput = z.infer<typeof sendEmailSchema>;
export type SubscribeEmailInput = z.infer<typeof subscribeEmailSchema>;
export type TriggerEmailSequenceInput = z.infer<typeof triggerEmailSequenceSchema>;
export type AcceptFunnelOfferInput = z.infer<typeof acceptFunnelOfferSchema>;
export type DeclineFunnelOfferInput = z.infer<typeof declineFunnelOfferSchema>;
export type InvoiceInput = z.infer<typeof invoiceInputSchema>;
export type UpdateInvoiceStatusInput = z.infer<typeof updateInvoiceStatusSchema>;
export type CreateShortlinkInput = z.infer<typeof createShortlinkSchema>;
export type UpdateShortlinkInput = z.infer<typeof updateShortlinkSchema>;
export type EnrollFreeCourseInput = z.infer<typeof enrollFreeCourseSchema>;
export type ProgressCompleteInput = z.infer<typeof progressCompleteSchema>;
export type ProgressWatchInput = z.infer<typeof progressWatchSchema>;
export type UserOnboardingInput = z.infer<typeof userOnboardingSchema>;
